import { supabase } from '../supabase';

export type OfficialCostCategory =
  | 'commission'
  | 'tax'
  | 'freight'
  | 'repacking'
  | 'other_costs';

export type CostTraceabilityLevel = 'line_item' | 'aggregate' | 'manual';

export interface ExtractedSettlementCostLine {
  id: string;
  originalName: string;
  amount: number;
  currency: string;
  suggestedOfficialCategory: OfficialCostCategory;
  confidence?: number | null;
  sourceLocation?: Record<string, unknown>;
  traceabilityLevel: CostTraceabilityLevel;
  reviewedOfficialCategory?: OfficialCostCategory;
  receiverCostMappingId?: string;
  /** Multi-shipment line shared with other settlements of its document. */
  shared?: boolean;
}

export interface ResolvedSettlementCostLine
  extends ExtractedSettlementCostLine {
  normalizedName: string;
  officialCategory: OfficialCostCategory;
  mappingStatus: 'saved' | 'new';
  mappingId?: string;
}

export interface ReceiverCostMapping {
  id: string;
  exporterId: number;
  exporterReceiverId: number;
  normalizedConcept: string;
  sampleOriginalName: string;
  officialCategory: OfficialCostCategory;
}

export interface SettlementSourceCostEditor {
  costLines: ExtractedSettlementCostLine[];
  canEditOriginalCosts: boolean;
  sourceFlow: 'fruitmarkets' | 'multi_shipment' | null;
}

export const OFFICIAL_COST_CATEGORIES: OfficialCostCategory[] = [
  'commission',
  'tax',
  'freight',
  'repacking',
  'other_costs',
];

export const OFFICIAL_COST_CATEGORY_LABELS: Record<
  OfficialCostCategory,
  string
> = {
  commission: 'Comisión',
  tax: 'Impuestos',
  freight: 'Flete',
  repacking: 'Repacking',
  other_costs: 'Otros costos',
};

export const isOfficialCostCategory = (
  value: unknown
): value is OfficialCostCategory =>
  typeof value === 'string' &&
  OFFICIAL_COST_CATEGORIES.includes(value as OfficialCostCategory);

/**
 * Stable V1 receiver-concept key. The exact source label is always retained
 * separately; this value is used only to find a reusable mapping.
 */
export const normalizeReceiverCostConcept = (value: string) =>
  value
    .normalize('NFKC')
    .replace(/[\u00a0\s]+/gu, ' ')
    .trim()
    .toLocaleUpperCase('en-US')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/gu, '-')
    .replace(/\s*([/&-])\s*/gu, '$1')
    .replace(/[.:;]+$/gu, '');

const mapReceiverCostMappingRow = (row: any): ReceiverCostMapping[] => {
  if (!isOfficialCostCategory(row.official_category)) return [];
  return [
    {
      id: row.id,
      exporterId: row.exporter_id,
      exporterReceiverId: row.exporter_receiver_id,
      normalizedConcept: row.normalized_concept,
      sampleOriginalName: row.sample_original_name,
      officialCategory: row.official_category,
    },
  ];
};

export const getReceiverCostMappings = async (
  exporterId: number,
  exporterReceiverId: number
): Promise<ReceiverCostMapping[]> => {
  const { data, error } = await supabase
    .from('receiver_cost_mappings')
    .select(
      'id, exporter_id, exporter_receiver_id, normalized_concept, sample_original_name, official_category'
    )
    .eq('exporter_id', exporterId)
    .eq('exporter_receiver_id', exporterReceiverId)
    .eq('status', 'active');

  if (error) throw error;

  return (data || []).flatMap(mapReceiverCostMappingRow);
};

export const supersedeReceiverCostMapping = async (
  mappingId: string,
  officialCategory: OfficialCostCategory
): Promise<ReceiverCostMapping> => {
  const { data, error } = await supabase.rpc(
    'supersede_receiver_cost_mapping',
    {
      p_mapping_id: mappingId,
      p_official_category: officialCategory,
    }
  );

  if (error) throw error;
  const mapping = (data || []).flatMap(mapReceiverCostMappingRow)[0];
  if (!mapping) throw new Error('No se pudo actualizar el mapeo de costos');
  return mapping;
};

export const getSettlementSourceCostLines = async (
  settlementId: number
): Promise<ExtractedSettlementCostLine[]> => {
  const { data, error } = await supabase.rpc(
    'get_settlement_source_cost_lines',
    { p_settlement_id: settlementId }
  );

  if (error) throw error;

  return (data || []).map((row: any) => ({
    id: String(row.id),
    originalName: String(row.original_name || ''),
    amount: Number(row.original_amount) || 0,
    currency: String(row.original_currency || ''),
    suggestedOfficialCategory: isOfficialCostCategory(
      row.suggested_official_category
    )
      ? row.suggested_official_category
      : 'other_costs',
    confidence:
      row.suggestion_confidence === null
        ? null
        : Number(row.suggestion_confidence),
    sourceLocation:
      row.source_location && typeof row.source_location === 'object'
        ? row.source_location
        : {},
    traceabilityLevel: ['line_item', 'aggregate', 'manual'].includes(
      row.traceability_level
    )
      ? row.traceability_level
      : 'line_item',
  }));
};

const mapSettlementSourceCostLine = (
  row: any
): ExtractedSettlementCostLine => ({
  id: String(row.id),
  originalName: String(row.original_name || ''),
  amount: Number(row.original_amount) || 0,
  currency: String(row.original_currency || ''),
  suggestedOfficialCategory: isOfficialCostCategory(
    row.suggested_official_category
  )
    ? row.suggested_official_category
    : 'other_costs',
  confidence:
    row.suggestion_confidence === null
      ? null
      : Number(row.suggestion_confidence),
  sourceLocation:
    row.source_location && typeof row.source_location === 'object'
      ? row.source_location
      : {},
  traceabilityLevel: ['line_item', 'aggregate', 'manual'].includes(
    row.traceability_level
  )
    ? row.traceability_level
    : 'line_item',
  reviewedOfficialCategory: isOfficialCostCategory(row.official_category)
    ? row.official_category
    : undefined,
  receiverCostMappingId:
    typeof row.receiver_cost_mapping_id === 'string'
      ? row.receiver_cost_mapping_id
      : undefined,
  shared: row.shared === true,
});

export const getSettlementSourceCostEditor = async (
  settlementId: number
): Promise<SettlementSourceCostEditor> => {
  const { data, error } = await supabase.rpc(
    'get_settlement_source_cost_editor',
    { p_settlement_id: settlementId }
  );

  if (error) throw error;

  const result = data && typeof data === 'object' ? (data as any) : {};
  const sourceFlow: SettlementSourceCostEditor['sourceFlow'] =
    result.source_flow === 'fruitmarkets' ||
    result.source_flow === 'multi_shipment'
      ? result.source_flow
      : null;

  return {
    costLines: Array.isArray(result.cost_lines)
      ? result.cost_lines.map(mapSettlementSourceCostLine)
      : [],
    canEditOriginalCosts: result.can_edit_original_costs === true,
    sourceFlow,
  };
};

export const updateStandardSettlementSourceCostLines = async (
  settlementId: number,
  costLines: ResolvedSettlementCostLine[]
): Promise<void> => {
  const { error } = await supabase.rpc(
    'update_standard_settlement_source_cost_lines',
    {
      p_settlement_id: settlementId,
      p_cost_lines: costLines.map((line) => ({
        id: line.id,
        originalName: line.originalName,
        amount: line.amount,
        currency: line.currency,
        officialCategory: line.officialCategory,
        suggestedOfficialCategory: line.suggestedOfficialCategory,
        confidence: line.confidence ?? null,
        sourceLocation: line.sourceLocation || {},
        traceabilityLevel: line.traceabilityLevel,
        receiverCostMappingId:
          line.receiverCostMappingId || line.mappingId || null,
      })),
    }
  );

  if (error) throw error;
};

/**
 * Records a bulk-uploaded cost as a manual cost line on the settlement's own
 * cost document. 'replace' first removes the settlement's own lines of the
 * same FM category. The caller still writes the FM columns.
 */
export const recordManualSettlementCosts = async (
  settlementId: number,
  lines: Array<{
    name: string;
    amount: number;
    currency: string;
    officialCategory: OfficialCostCategory;
  }>,
  mode: 'add' | 'replace'
): Promise<void> => {
  const { error } = await supabase.rpc('record_manual_settlement_costs', {
    p_settlement_id: settlementId,
    p_lines: lines,
    p_mode: mode,
  });
  if (error) throw error;
};

/**
 * Settlements a bulk cost upload would refuse: blocked ones, and in Replace
 * mode those whose category comes from shared multi-shipment costs.
 */
export const checkManualSettlementCosts = async (
  settlementIds: number[],
  officialCategory: OfficialCostCategory,
  mode: 'add' | 'replace'
): Promise<Array<{ settlementId: number; reason: 'blocked' | 'shared' }>> => {
  const { data, error } = await supabase.rpc('check_manual_settlement_costs', {
    p_settlement_ids: settlementIds,
    p_official_category: officialCategory,
    p_mode: mode,
  });
  if (error) throw error;
  return (data || []).map((row: any) => ({
    settlementId: Number(row.settlement_id),
    reason: row.reason,
  }));
};

export const resolveReceiverCostLines = (
  costLines: ExtractedSettlementCostLine[],
  mappings: ReceiverCostMapping[],
  pendingCategories: Record<string, OfficialCostCategory> = {}
): ResolvedSettlementCostLine[] => {
  const mappingsByConcept = new Map(
    mappings.map((mapping) => [mapping.normalizedConcept, mapping])
  );

  return costLines.map((line) => {
    const normalizedName = normalizeReceiverCostConcept(line.originalName);
    const mapping = mappingsByConcept.get(normalizedName);
    return {
      ...line,
      normalizedName,
      officialCategory:
        pendingCategories[normalizedName] ||
        line.reviewedOfficialCategory ||
        mapping?.officialCategory ||
        line.suggestedOfficialCategory,
      mappingStatus: mapping || line.receiverCostMappingId ? 'saved' : 'new',
      mappingId: line.receiverCostMappingId || mapping?.id,
    };
  });
};
